"use client";

import React, { useState } from "react";
import { Clock, FileText, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTicketHooks } from "@/features/tickets/hooks/ticket-hooks";
import { useCustomTemplates } from "@/lib/hooks/settings/use-custom-templates";
import { BaseTemplate } from "@/components/shared/UniversalEntityPage/types";
import { getTemplates, type TemplateType } from "./utils/getTemplates";
import { TemplatePreview } from "./components/TemplatePreview";
import { TemplateSelectUI } from "./components/TemplateSelectUI";

export interface ReusableTemplateSelectorProps {
  templateType: TemplateType;
  selectedTemplate: BaseTemplate | null;
  onTemplateSelect: (template: BaseTemplate | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  customTemplates?: BaseTemplate[];
  fieldMappings?: Record<string, string>;
  showClearOption?: boolean;
  icon?: React.ReactNode;
  label?: string;
  /** When true, expand inline (search + list below) instead of popover. Use in modals. */
  expandInline?: boolean;
}

export const ReusableTemplateSelector: React.FC<ReusableTemplateSelectorProps> = ({
  templateType,
  selectedTemplate,
  onTemplateSelect,
  placeholder = "Select a template...",
  searchPlaceholder = "Search templates...",
  className = "",
  customTemplates = [],
  showClearOption = true,
  icon,
  label = "Template",
  expandInline = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Hooks for different template types
  const ticketHooks = useTicketHooks();
  const { customTemplates: userCustomTemplates = [] } = useCustomTemplates();
  const { data: ticketTemplates = [] } = ticketHooks.useTemplates();

  // Get templates using extracted utility
  const templates = getTemplates({
    templateType,
    ticketTemplates: Array.isArray(ticketTemplates) ? ticketTemplates : [],
    userCustomTemplates: Array.isArray(userCustomTemplates) ? userCustomTemplates : [],
    customTemplates: Array.isArray(customTemplates) ? customTemplates : [],
  });

  const handleTemplateChange = (value: string) => {
    if (value === "none") {
      onTemplateSelect(null);
      return;
    }

    const selectedOption = templates.find((option) => option.id === value);
    if (selectedOption) {
      onTemplateSelect(selectedOption);
    }
    setOpen(false);
  };

  const currentValue = selectedTemplate?.id || "none";

  // Filter templates based on search
  const filteredTemplates = templates.filter((option) => {
    if (!searchValue) return true;
    const searchLower = searchValue.toLowerCase();
    return (
      option.name.toLowerCase().includes(searchLower) ||
      (option.description && option.description.toLowerCase().includes(searchLower)) ||
      (option.category && option.category.toLowerCase().includes(searchLower))
    );
  });

  // Get icon based on template type
  const getIcon = () => {
    if (icon) return icon;
    switch (templateType) {
      case "ticket":
        return <Ticket className="h-4 w-4 text-gray-500" />;
      case "time-entry":
        return <Clock className="h-4 w-4 text-gray-500" />;
      case "task":
        return <FileText className="h-4 w-4 text-gray-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        {getIcon()}
        <label className="block text-sm font-semibold">{label}</label>
        {selectedTemplate && showClearOption && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onTemplateSelect(null)}
            className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </Button>
        )}
      </div>

      <div className="flex gap-4 items-start">
        <TemplateSelectUI
          templates={templates}
          filteredTemplates={filteredTemplates}
          selectedTemplate={selectedTemplate}
          currentValue={currentValue}
          templateType={templateType}
          open={open}
          onOpenChange={setOpen}
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          showClearOption={showClearOption}
          onTemplateChange={handleTemplateChange}
          expandInline={expandInline}
        />
      </div>

      {/* When expandInline (e.g. in modal), form fields are already filled from template — skip preview to avoid a second block that looks like another form and extra blank space */}
      {selectedTemplate && !expandInline && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950/40 dark:border-blue-800">
          <TemplatePreview template={selectedTemplate} templateType={templateType} />
        </div>
      )}

      <div className="text-xs text-gray-500 mt-2">
        💡 Hover over a template to preview it before selecting.
      </div>
    </div>
  );
};

export default ReusableTemplateSelector;
